// React imports
import React, { useState, useEffect } from 'react';

// General imports
import { useLocation } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import _ from 'lodash';

// Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarCircle, faBuilding, faCaretRight, faCaretLeft, faComments, faListTree,
    faMagnifyingGlassDollar, faCommentDollar, faMoneyCheckDollarPen, faRobot } from "@fortawesome/pro-solid-svg-icons";

// Mui imports
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { blue } from '@mui/material/colors';

// Local imports
import RatesPane from './SidePane.rates';
import FilesAndCommentsPane from './SidePane.filesandcomments';
import OpportunityBuyingRatesPane from './SidePane.opportunitybuyingrates';
import RfqsPane from './SidePane.rfqs';
import OpportunitiesPane from './SidePane.opportunities';
import AccountPane from './SidePane.account';
import StructurePane from './SidePane.structure';
import AiAgentPane from './SidePane.aiagent';

function Sidepane(props) {
    const context = useOutletContext();
    const { minimizedSearch, setMinimizedSearch, fullscreen = false, opportunities, onUpdateOpportunity, org, me, accounts } = _.isUndefined(props.minimizedSearch) ? { ...context, fullscreen: true}  : props;
    const theme = useTheme();
    const [ page, setPage ] = useState('rates');
    const location = useLocation();
    const [ innerOpportunity, setInnerOpportunity ] = useState(null);
    const [ innerSearchPaneLoadFromOpportunity, setInnerSearchPaneLoadFromOpportunity ] = useState(null);

    const pathname = location.pathname;
    let opportunity = null;

    useEffect(() => {
        window.onUpdateSearchPaneLoadFromOpportunity = window.onUpdateSearchPaneLoadFromOpportunity || [];
        window.onUpdateSearchPaneLoadFromOpportunity.push((e) => {
            console.log('[Sidepane] onUpdateSearchPaneLoadFromOpportunity', e);
            setInnerSearchPaneLoadFromOpportunity(e);
        });
        return () => {
            window.onUpdateSearchPaneLoadFromOpportunity = window.onUpdateSearchPaneLoadFromOpportunity.filter(fn => fn !== setInnerSearchPaneLoadFromOpportunity);
        };
    }, []);

    useEffect(() => {
        let match;
        if (match = pathname.match(/.*\/opportunities\/([\w-]+)/)) {
            opportunity = opportunities.find(opportunity => opportunity.id === match[1]);
            setInnerOpportunity(opportunity);
        } else if (innerSearchPaneLoadFromOpportunity) {
            opportunity = _.cloneDeep(opportunities.find(opportunity => opportunity.id === innerSearchPaneLoadFromOpportunity?.opportunityId));
            if (opportunity && innerSearchPaneLoadFromOpportunity) {
                opportunity.routes = innerSearchPaneLoadFromOpportunity.routes;
                opportunity.transportationMethod = innerSearchPaneLoadFromOpportunity.transportationMethod;
                opportunity.products = innerSearchPaneLoadFromOpportunity.products;
                opportunity.source = innerSearchPaneLoadFromOpportunity.source;
            }
            setInnerOpportunity(opportunity);
        } else {
            setInnerOpportunity(null);
        }
    }, [opportunities, pathname, innerSearchPaneLoadFromOpportunity]);

    // Check if hash indicates we should open a specific panel
    useEffect(() => {
        if (location.hash === '#rfqs' && innerOpportunity) {
            setPage('rfqs');
        }
    }, [location.hash, innerOpportunity]);

    // Listen for requests to open a specific side pane page
    useEffect(() => {
        const handler = (e) => {
            setMinimizedSearch(false);
            setPage(e.detail);
        };
        window.addEventListener('open-sidepane-page', handler);
        return () => window.removeEventListener('open-sidepane-page', handler);
    }, []);

    const actualPage = (() => innerOpportunity ? page : page === 'opportunities' ? 'opportunities' : page === 'ai-agent' ? 'ai-agent' : 'rates')();

    return (
        <Paper sx={{ background: theme.palette.background.default, height: '100%', position: 'fixed', right: '0px', top: '0px', width: fullscreen ? '100%' : minimizedSearch ? '30px' : '557px', transition: 'width 200ms', zIndex: '1000000', display: 'flex', elevation: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', background: theme.palette.searchbarbackground, minWidth: '30px', borderRight: `1px solid ${theme.palette.border1}` }}>
                <ButtonGroup size='small' orientation='vertical'>
                    {!fullscreen && (
                        <IconButton onClick={() => setMinimizedSearch(!minimizedSearch)} sx={{ fontSize: '18px' }}>
                            {!minimizedSearch && <FontAwesomeIcon icon={faCaretRight} />}
                            {minimizedSearch && <FontAwesomeIcon icon={faCaretLeft} />}
                        </IconButton>
                    )}
                    <Divider />
                    <Tooltip
                        title='Search Rates'
                        placement='left-end'
                    >
                        <IconButton onClick={() => { setMinimizedSearch(false); setPage('rates') }} color={actualPage !== 'rates' ? undefined : 'primary'} sx={{ fontSize: '18px', borderRadius: 0, borderRight: actualPage !== 'rates' || minimizedSearch ? '' : `2px solid ${blue[500]}` }}>
                            <FontAwesomeIcon icon={faMagnifyingGlassDollar} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title='My Opportunities'
                        placement='left-end'
                    >
                        <IconButton onClick={() => { setMinimizedSearch(false); setPage('opportunities'); }} color={actualPage !== 'opportunities' ? undefined : 'primary'} sx={{ fontSize: '18px', borderRadius: 0, borderRight: actualPage !== 'opportunities' || minimizedSearch ? '' : `2px solid ${blue[500]}` }}>
                            <FontAwesomeIcon icon={faMoneyCheckDollarPen} />
                        </IconButton>
                    </Tooltip>
                    {innerOpportunity && (
                        <>
                            <Divider />
                            <Tooltip
                                title='Cost Center'
                                placement='left-end'
                            >
                                <IconButton onClick={() => { setMinimizedSearch(false); setPage('opportunity-buying-rates'); }} color={actualPage !== 'opportunity-buying-rates' ? undefined : 'primary'} sx={{ fontSize: '18px', borderRadius: 0, borderRight: actualPage !== 'opportunity-buying-rates' || minimizedSearch ? '' : `2px solid ${blue[500]}` }}>
                                    <FontAwesomeIcon icon={faDollarCircle} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {innerOpportunity && (
                        <>
                            <Divider />
                            <Tooltip
                                title='RFQs to Suppliers'
                                placement='left-end'
                            >
                                <IconButton onClick={() => { setMinimizedSearch(false); setPage('rfqs'); }} color={actualPage !== 'rfqs' ? undefined : 'primary'} sx={{ fontSize: '18px', borderRadius: 0, borderRight: actualPage !== 'rfqs' || minimizedSearch ? '' : `2px solid ${blue[500]}` }}>
                                    <FontAwesomeIcon icon={faCommentDollar} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {innerOpportunity && (
                        <>
                            <Divider />
                            <Tooltip
                                title='Client Account Details'
                                placement='left-end'
                            >
                                <IconButton disabled={!innerOpportunity.account} onClick={() => { setMinimizedSearch(false); setPage('accounts'); }} color={actualPage !== 'accounts' ? undefined : 'primary'} sx={{ fontSize: '18px', borderRadius: 0, borderRight: actualPage !== 'accounts' || minimizedSearch ? '' : `2px solid ${blue[500]}` }}>
                                    <FontAwesomeIcon icon={faBuilding} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {innerOpportunity && (
                        <>
                            <Divider />
                            <Tooltip
                                title='Updates & Comments'
                                placement='left-end'
                            >
                                <IconButton disabled={!innerOpportunity.account} onClick={() => { setMinimizedSearch(false); setPage('files'); }} color={actualPage !== 'files' ? undefined : 'primary'} sx={{ fontSize: '18px', borderRadius: 0, borderRight: actualPage !== 'files' || minimizedSearch ? '' : `2px solid ${blue[500]}` }}>
                                    <FontAwesomeIcon icon={faComments} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    <Divider />
                    <Tooltip
                        title='Monada AI Assistant'
                        placement='left-end'
                    >
                        <IconButton onClick={() => { setMinimizedSearch(false); setPage('ai-agent'); }} color={actualPage !== 'ai-agent' ? undefined : 'primary'} sx={{ fontSize: '18px', borderRadius: 0, borderRight: actualPage !== 'ai-agent' || minimizedSearch ? '' : `2px solid ${blue[500]}` }}>
                            <FontAwesomeIcon icon={faRobot} />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>
            </Box>
            <Box sx={{ display: actualPage === 'rates' ? 'block' : 'none' }}>
                <RatesPane {...props} organization={org} opportunity={innerOpportunity} />
            </Box>
            {actualPage === 'opportunities' && <OpportunitiesPane {...props} organization={org} opportunity={innerOpportunity} />}
            {actualPage === 'files' && <FilesAndCommentsPane {...props} me={me} organization={org} opportunity={innerOpportunity} onUpdateOpportunity={onUpdateOpportunity} />}
            {actualPage === 'opportunity-buying-rates' && <OpportunityBuyingRatesPane {...props} me={me} organization={org} opportunity={innerOpportunity} onUpdateOpportunity={onUpdateOpportunity} />}
            {actualPage === 'rfqs' && <RfqsPane {...props} me={me} organization={org} opportunity={innerOpportunity} onUpdateOpportunity={onUpdateOpportunity} accounts={accounts} />}
            {actualPage === 'accounts' && <AccountPane {...props} me={me} organization={org} opportunity={innerOpportunity} />}
            {actualPage === 'structure' && <StructurePane {...props} me={me} organization={org} opportunity={innerOpportunity} />}
            {actualPage === 'ai-agent' && <AiAgentPane {...props} me={me} organization={org} opportunity={innerOpportunity} />}
        </Paper>
    );
}

export default Sidepane;